// In-process verification for notification grouping + reply-from-notification.
//
// Hits four pure-function modules:
//   A. NotificationCategory — descriptor shape (action ids, text-input
//      flags, placeholders, category set completeness, browser-action shape).
//   B. NotificationActionHandler — outcome decisions for every
//      (actionId × ctx-flag) combination that matters at runtime.
//   C. NotificationGroupingPolicy — channel vs 1on1 vs null bucketing,
//      singular / plural summary strings.
//   D. Hardening invariants — closed-enum has exactly 3 values; ignored
//      reasons never embed user cleartext.
//
// All cases are pure (no Notification API, no Service Worker, no network)
// so the suite runs in the same dev-mode autorun as SVR / mentions tests
// via `SelfTestRunner`.
//
// Mirror of `kaether_for_ios/Stoat/E2EE/NotificationGroupingSelfTest.swift`.

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

import {
  ALL_NOTIFICATION_ACTION_IDS,
  NotificationCategoryBuilder,
} from "./NotificationCategory";
import type { NotificationActionDescriptor } from "./NotificationCategory";

import {
  NotificationActionDispatcher,
} from "./NotificationActionHandler";
import type { NotificationActionContext } from "./NotificationActionHandler";

import { NotificationGroupingPolicy } from "./NotificationGroupingPolicy";

SelfTestRunner.register({
  name: "Notification grouping (categories + reply-from-notification)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // A. Category builder
      testAllCategoriesCount(),
      testMessageCategoryHasThreeActions(),
      testQuickReplyIsTextInput(),
      testMarkReadAndMute1hAreNotTextInput(),
      testCallCategoryHasZeroActions(),
      testMentionCategoryId(),
      testBrowserActionShapeQuickReply(),
      testBrowserActionShapeMarkRead(),

      // B. Action dispatcher
      testQuickReplyBlockedSender(),
      testQuickReplyEmptyText(),
      testQuickReplyWhitespaceOnly(),
      testQuickReplyTooLong(),
      testQuickReplyHappyPath(),
      testMarkReadAlwaysSucceeds(),
      testMute1hWhenNotMuted(),
      testMute1hWhenAlreadyMuted(),

      // C. Grouping
      testThreadIdChannelPresent(),
      testThreadIdOneOnOne(),
      testThreadIdBothNull(),
      testSummarySingular(),
      testSummaryPlural(),

      // D. Hardening
      testActionIdEnumIsClosed(),
      testIgnoredReasonsNeverEmbedCleartext(),
    ]),
});

// ---------------------------------------------------------------------------
// A. Category builder
// ---------------------------------------------------------------------------

function testAllCategoriesCount(): TestCase {
  const all = NotificationCategoryBuilder.allCategories();
  const ids = all.map((c) => c.id);
  const ok =
    all.length === 3 &&
    ids.includes("message") &&
    ids.includes("mention") &&
    ids.includes("call");
  return ok
    ? pass("allCategories returns exactly the three category descriptors")
    : fail(
        "allCategories returns exactly the three category descriptors",
        `got ids=${JSON.stringify(ids)}`,
      );
}

function testMessageCategoryHasThreeActions(): TestCase {
  const cat = NotificationCategoryBuilder.messageCategory();
  const ids = cat.actions.map((a) => a.id);
  const ok =
    cat.actions.length === 3 &&
    ids[0] === "quickReply" &&
    ids[1] === "markRead" &&
    ids[2] === "mute1h";
  return ok
    ? pass("messageCategory has [quickReply, markRead, mute1h] in order")
    : fail(
        "messageCategory has [quickReply, markRead, mute1h] in order",
        `got actions=${JSON.stringify(ids)}`,
      );
}

function testQuickReplyIsTextInput(): TestCase {
  const cat = NotificationCategoryBuilder.messageCategory();
  const reply = cat.actions.find((a) => a.id === "quickReply");
  if (!reply) {
    return fail(
      "quickReply action is text-input with non-null placeholder",
      "no quickReply action found in messageCategory",
    );
  }
  const ok =
    reply.isTextInput === true &&
    reply.inputButtonTitle !== null &&
    (reply.inputButtonTitle?.length ?? 0) > 0 &&
    reply.inputPlaceholder !== null &&
    (reply.inputPlaceholder?.length ?? 0) > 0;
  return ok
    ? pass("quickReply action is text-input with non-null placeholder")
    : fail(
        "quickReply action is text-input with non-null placeholder",
        `isTextInput=${reply.isTextInput} button=${String(reply.inputButtonTitle)} placeholder=${String(reply.inputPlaceholder)}`,
      );
}

function testMarkReadAndMute1hAreNotTextInput(): TestCase {
  const cat = NotificationCategoryBuilder.messageCategory();
  const markRead = cat.actions.find((a) => a.id === "markRead");
  const mute1h = cat.actions.find((a) => a.id === "mute1h");
  if (!markRead || !mute1h) {
    return fail(
      "markRead and mute1h actions are not text-input",
      "missing markRead or mute1h action in messageCategory",
    );
  }
  const ok = !markRead.isTextInput && !mute1h.isTextInput;
  return ok
    ? pass("markRead and mute1h actions are not text-input")
    : fail(
        "markRead and mute1h actions are not text-input",
        `markRead.isTextInput=${markRead.isTextInput} mute1h.isTextInput=${mute1h.isTextInput}`,
      );
}

function testCallCategoryHasZeroActions(): TestCase {
  const cat = NotificationCategoryBuilder.callCategory();
  const ok = cat.actions.length === 0 && cat.id === "call";
  return ok
    ? pass("callCategory has zero actions (browser call-ringing UI owns accept/decline)")
    : fail(
        "callCategory has zero actions (browser call-ringing UI owns accept/decline)",
        `actions.length=${cat.actions.length} id=${cat.id}`,
      );
}

function testMentionCategoryId(): TestCase {
  const cat = NotificationCategoryBuilder.mentionCategory();
  const ok = cat.id === "mention";
  return ok
    ? pass("mentionCategory descriptor id is 'mention'")
    : fail(
        "mentionCategory descriptor id is 'mention'",
        `got id=${cat.id}`,
      );
}

function testBrowserActionShapeQuickReply(): TestCase {
  const cat = NotificationCategoryBuilder.messageCategory();
  const reply = cat.actions.find((a) => a.id === "quickReply");
  if (!reply) {
    return fail(
      "quickReply toBrowserAction produces {action, title, type:'text', placeholder}",
      "no quickReply action found",
    );
  }
  const browser = NotificationCategoryBuilder.toBrowserAction(reply);
  const ok =
    browser.action === "quickReply" &&
    browser.title === "Reply" &&
    browser.type === "text" &&
    typeof browser.placeholder === "string" &&
    (browser.placeholder?.length ?? 0) > 0;
  return ok
    ? pass("quickReply toBrowserAction produces {action, title, type:'text', placeholder}")
    : fail(
        "quickReply toBrowserAction produces {action, title, type:'text', placeholder}",
        `got ${JSON.stringify(browser)}`,
      );
}

function testBrowserActionShapeMarkRead(): TestCase {
  const cat = NotificationCategoryBuilder.messageCategory();
  const markRead = cat.actions.find((a) => a.id === "markRead");
  if (!markRead) {
    return fail(
      "markRead toBrowserAction produces {action, title} with no type field",
      "no markRead action found",
    );
  }
  const browser = NotificationCategoryBuilder.toBrowserAction(markRead);
  const ok =
    browser.action === "markRead" &&
    browser.title === "Mark as read" &&
    browser.type === undefined;
  return ok
    ? pass("markRead toBrowserAction produces {action, title} with no type field")
    : fail(
        "markRead toBrowserAction produces {action, title} with no type field",
        `got ${JSON.stringify(browser)}`,
      );
}

// ---------------------------------------------------------------------------
// B. Action dispatcher
// ---------------------------------------------------------------------------

function makeCtx(
  overrides: Partial<NotificationActionContext> & {
    actionId: NotificationActionContext["actionId"];
  },
): NotificationActionContext {
  return {
    actionId: overrides.actionId,
    threadIdentifier: overrides.threadIdentifier ?? "thread-default",
    senderUserId: overrides.senderUserId ?? "sender-default",
    messageId: overrides.messageId ?? "msg-default",
    userText: overrides.userText ?? null,
    isThreadMuted: overrides.isThreadMuted ?? false,
    isSenderBlocked: overrides.isSenderBlocked ?? false,
  };
}

function testQuickReplyBlockedSender(): TestCase {
  const ctx = makeCtx({
    actionId: "quickReply",
    userText: "hi",
    isSenderBlocked: true,
  });
  const outcome = NotificationActionDispatcher.dispatch(ctx);
  const ok =
    outcome.kind === "ignored" && outcome.reason === "blocked sender";
  return ok
    ? pass("quickReply with blocked sender → ignored('blocked sender')")
    : fail(
        "quickReply with blocked sender → ignored('blocked sender')",
        `got ${JSON.stringify(outcome)}`,
      );
}

function testQuickReplyEmptyText(): TestCase {
  const ctx = makeCtx({ actionId: "quickReply", userText: "" });
  const outcome = NotificationActionDispatcher.dispatch(ctx);
  const ok =
    outcome.kind === "ignored" && outcome.reason === "empty text";
  return ok
    ? pass("quickReply with empty text → ignored('empty text')")
    : fail(
        "quickReply with empty text → ignored('empty text')",
        `got ${JSON.stringify(outcome)}`,
      );
}

function testQuickReplyWhitespaceOnly(): TestCase {
  const ctx = makeCtx({ actionId: "quickReply", userText: "   \n\t  " });
  const outcome = NotificationActionDispatcher.dispatch(ctx);
  const ok =
    outcome.kind === "ignored" && outcome.reason === "empty text";
  return ok
    ? pass("quickReply with whitespace-only text → ignored('empty text') after trim")
    : fail(
        "quickReply with whitespace-only text → ignored('empty text') after trim",
        `got ${JSON.stringify(outcome)}`,
      );
}

function testQuickReplyTooLong(): TestCase {
  const oversize = "A".repeat(
    NotificationActionDispatcher.maxReplyTextCount + 1,
  );
  const ctx = makeCtx({ actionId: "quickReply", userText: oversize });
  const outcome = NotificationActionDispatcher.dispatch(ctx);
  const ok = outcome.kind === "ignored" && outcome.reason === "too long";
  return ok
    ? pass("quickReply with >maxReplyTextCount → ignored('too long')")
    : fail(
        "quickReply with >maxReplyTextCount → ignored('too long')",
        `got ${JSON.stringify(outcome)} len=${oversize.length}`,
      );
}

function testQuickReplyHappyPath(): TestCase {
  const ctx = makeCtx({
    actionId: "quickReply",
    threadIdentifier: "t-1",
    senderUserId: "alice",
    userText: "  hello  ",
  });
  const outcome = NotificationActionDispatcher.dispatch(ctx);
  const ok =
    outcome.kind === "sendReply" &&
    outcome.threadIdentifier === "t-1" &&
    outcome.senderUserId === "alice" &&
    outcome.text === "hello";
  return ok
    ? pass("quickReply happy path → sendReply with trimmed text")
    : fail(
        "quickReply happy path → sendReply with trimmed text",
        `got ${JSON.stringify(outcome)}`,
      );
}

function testMarkReadAlwaysSucceeds(): TestCase {
  // Even when sender is blocked / thread is muted, marking read is a
  // local-only action with no outbound side channel — it always passes.
  const ctx = makeCtx({
    actionId: "markRead",
    messageId: "msg-42",
    isThreadMuted: true,
    isSenderBlocked: true,
  });
  const outcome = NotificationActionDispatcher.dispatch(ctx);
  const ok = outcome.kind === "markRead" && outcome.messageId === "msg-42";
  return ok
    ? pass("markRead always emits markRead regardless of mute/block flags")
    : fail(
        "markRead always emits markRead regardless of mute/block flags",
        `got ${JSON.stringify(outcome)}`,
      );
}

function testMute1hWhenNotMuted(): TestCase {
  const ctx = makeCtx({
    actionId: "mute1h",
    threadIdentifier: "general",
    isThreadMuted: false,
  });
  const outcome = NotificationActionDispatcher.dispatch(ctx);
  const ok =
    outcome.kind === "muteThread" &&
    outcome.threadIdentifier === "general" &&
    outcome.durationSeconds === 3_600;
  return ok
    ? pass("mute1h when not muted → muteThread(3600)")
    : fail(
        "mute1h when not muted → muteThread(3600)",
        `got ${JSON.stringify(outcome)}`,
      );
}

function testMute1hWhenAlreadyMuted(): TestCase {
  const ctx = makeCtx({
    actionId: "mute1h",
    threadIdentifier: "general",
    isThreadMuted: true,
  });
  const outcome = NotificationActionDispatcher.dispatch(ctx);
  const ok =
    outcome.kind === "ignored" && outcome.reason === "already muted";
  return ok
    ? pass("mute1h when already muted → ignored('already muted')")
    : fail(
        "mute1h when already muted → ignored('already muted')",
        `got ${JSON.stringify(outcome)}`,
      );
}

// ---------------------------------------------------------------------------
// C. Grouping
// ---------------------------------------------------------------------------

function testThreadIdChannelPresent(): TestCase {
  const id = NotificationGroupingPolicy.threadIdentifier("chan-1", null);
  const ok = id === "chan-1";
  return ok
    ? pass("threadIdentifier returns channelId verbatim when present")
    : fail(
        "threadIdentifier returns channelId verbatim when present",
        `got=${String(id)}`,
      );
}

function testThreadIdOneOnOne(): TestCase {
  const id = NotificationGroupingPolicy.threadIdentifier(null, "alice");
  const ok = id === "1on1:alice";
  return ok
    ? pass("threadIdentifier returns '1on1:<peer>' when channelId is null")
    : fail(
        "threadIdentifier returns '1on1:<peer>' when channelId is null",
        `got=${String(id)}`,
      );
}

function testThreadIdBothNull(): TestCase {
  const id = NotificationGroupingPolicy.threadIdentifier(null, null);
  const ok = id === null;
  return ok
    ? pass("threadIdentifier returns null when both inputs are null")
    : fail(
        "threadIdentifier returns null when both inputs are null",
        `got=${String(id)}`,
      );
}

function testSummarySingular(): TestCase {
  const s = NotificationGroupingPolicy.summary(1);
  const ok = s === "1 new message";
  return ok
    ? pass("summary(1) → '1 new message' (singular)")
    : fail("summary(1) → '1 new message' (singular)", `got="${s}"`);
}

function testSummaryPlural(): TestCase {
  const two = NotificationGroupingPolicy.summary(2);
  const ten = NotificationGroupingPolicy.summary(10);
  const ok = two === "2 new messages" && ten === "10 new messages";
  return ok
    ? pass("summary(N) → 'N new messages' for N >= 2")
    : fail(
        "summary(N) → 'N new messages' for N >= 2",
        `got two="${two}" ten="${ten}"`,
      );
}

// ---------------------------------------------------------------------------
// D. Hardening
// ---------------------------------------------------------------------------

function testActionIdEnumIsClosed(): TestCase {
  // The union type is closed by TypeScript's type system. We assert the
  // *case count* at runtime via the ALL_NOTIFICATION_ACTION_IDS array so an
  // accidental new value forces a deliberate update here and in the
  // dispatcher.
  const ids = ALL_NOTIFICATION_ACTION_IDS;
  const ok =
    ids.length === 3 &&
    ids.includes("quickReply") &&
    ids.includes("markRead") &&
    ids.includes("mute1h");
  return ok
    ? pass("NotificationActionId is a closed union (exactly 3 values)")
    : fail(
        "NotificationActionId is a closed union (exactly 3 values)",
        `got count=${ids.length} values=${JSON.stringify(ids)}`,
      );
}

function testIgnoredReasonsNeverEmbedCleartext(): TestCase {
  // The reason strings must not be formatted from `userText`,
  // `senderUserId`, or `threadIdentifier` so a debug log of the
  // dispatcher's output cannot leak the cleartext the user typed.
  // Drive the dispatcher with a marker payload and confirm the marker
  // never appears in any ignored reason.
  const MARKER = "MARKER-9F1B-LEAK-CHECK";
  const inputs: NotificationActionContext[] = [
    makeCtx({ actionId: "quickReply", userText: MARKER, isSenderBlocked: true }),
    makeCtx({ actionId: "quickReply", userText: "" }),
    makeCtx({ actionId: "quickReply", userText: "   " }),
    makeCtx({ actionId: "quickReply", userText: MARKER.repeat(200) }),
    makeCtx({ actionId: "mute1h", threadIdentifier: MARKER, isThreadMuted: true }),
  ];

  for (const ctx of inputs) {
    const outcome = NotificationActionDispatcher.dispatch(ctx);
    if (outcome.kind === "ignored" && outcome.reason.includes(MARKER)) {
      return fail(
        "ignored reasons never embed userText / threadIdentifier",
        `reason leaked marker: ${outcome.reason}`,
      );
    }
  }

  // Spot-check that the full literal reason set is exactly the four
  // we documented.
  const expected = new Set(["blocked sender", "empty text", "too long", "already muted"]);
  const observed = new Set<string>();
  for (const ctx of inputs) {
    const outcome = NotificationActionDispatcher.dispatch(ctx);
    if (outcome.kind === "ignored") {
      observed.add(outcome.reason);
    }
  }
  const ok =
    observed.size === expected.size &&
    [...expected].every((r) => observed.has(r));
  return ok
    ? pass("ignored reasons are the four constant literals only")
    : fail(
        "ignored reasons are the four constant literals only",
        `observed=${JSON.stringify([...observed].sort())} expected=${JSON.stringify([...expected].sort())}`,
      );
}
